import { create } from 'zustand';

export enum ModelTypeEnum {
  Standing = 'standing',
  Bent = 'bent',
}
export enum LayerTypeEnum {
  Sagittal = 'sagittal',
  General = 'general',
  Map = 'map',
  ThreeDModel = 'three_d_model',
}

export enum MetricKeyEnum {
  Standing = 'standing',
  Bent = 'bent',
  BentDeviation = 'bent_deviation',
  ShoulderAsymmetry = 'shoulder_asymmetry',
  TrunkShift = 'trunk_shift',
  PlumbShift = 'plumb_shift',
  MidSacralPlane = 'mid_sacral_plane',
  CenterOfMass = 'center_of_mass',
  CenterOfMassAboveHips = 'center_of_mass_above_hips',
  HipToEar = 'hip_to_ear',
  KneeBend = 'knee_bend',
  AnkleFlexion = 'ankle_flexion',
  SurfaceSpine = 'surface_spine',
  MaxLowerSagittal = 'max_lower_sagittal',
  MaxUpperSagittal = 'max_upper_sagittal',
  SagittalBalancePlane = 'sagittal_balance_plane',
  MidSagittalPlane = 'mid_sagittal_plane',
  AiPredictedCobbAngle = 'ai_predicted_cobb_angle',
}

interface Metric {
  key: MetricKeyEnum;
  tableName: string | undefined;
  toggleName: string | undefined;
  layerKey: string | undefined;
  unit: string | undefined;
  description: string;
  modelType: ModelTypeEnum;
  layerType: LayerTypeEnum;
}

export const metrics: Record<MetricKeyEnum, Metric> = {
  [MetricKeyEnum.Standing]: {
    key: MetricKeyEnum.Standing,
    modelType: ModelTypeEnum.Standing,
    layerType: LayerTypeEnum.ThreeDModel,
    tableName: undefined,
    toggleName: 'Standing Mesh',
    layerKey: 'standing_mesh',
    unit: undefined,
    description: '',
  },
  [MetricKeyEnum.Bent]: {
    key: MetricKeyEnum.Bent,
    modelType: ModelTypeEnum.Bent,
    layerType: LayerTypeEnum.ThreeDModel,
    tableName: undefined,
    toggleName: 'Bent Mesh',
    layerKey: 'bent_mesh',
    unit: undefined,
    description: '',
  },
  [MetricKeyEnum.BentDeviation]: {
    key: MetricKeyEnum.BentDeviation,
    modelType: ModelTypeEnum.Bent,
    layerType: LayerTypeEnum.Map,
    tableName: undefined,
    toggleName: 'Bent Deviation Map',
    layerKey: 'bent_deviation_map',
    unit: undefined,
    description:
      'A color-coded 3D image showing how evenly the back bends between the right and left sides. Blue areas are low, yellow areas are more raised.',
  },
  [MetricKeyEnum.ShoulderAsymmetry]: {
    key: MetricKeyEnum.ShoulderAsymmetry,
    modelType: ModelTypeEnum.Standing,
    layerType: LayerTypeEnum.General,
    tableName: 'Shoulder Asymmetry',
    toggleName: 'Shoulder Asymmetry',
    layerKey: 'shoulder_asymmetry',
    unit: '°',
    description: 'Shows if one shoulder is higher than the other.',
  },
  [MetricKeyEnum.PlumbShift]: {
    key: MetricKeyEnum.PlumbShift,
    modelType: ModelTypeEnum.Standing,
    layerType: LayerTypeEnum.General,
    tableName: 'Plumb Shift',
    toggleName: 'Plumb Shift',
    layerKey: 'plumb_shift',
    unit: 'mm',
    description:
      'How much your neck is shifted to the left or right compared to your hips.',
  },
  [MetricKeyEnum.TrunkShift]: {
    key: MetricKeyEnum.TrunkShift,
    modelType: ModelTypeEnum.Standing,
    layerType: LayerTypeEnum.General,
    tableName: 'Trunk Shift',
    toggleName: 'Trunk Shift',
    layerKey: 'trunk_shift',
    unit: 'mm',
    description: 'How far your upper body leans sideways from your hips.',
  },
  [MetricKeyEnum.MidSacralPlane]: {
    key: MetricKeyEnum.MidSacralPlane,
    modelType: ModelTypeEnum.Standing,
    layerType: LayerTypeEnum.General,
    tableName: 'Mid Sacral Plane',
    toggleName: 'Mid Sacral Plane',
    layerKey: 'mid_sacral_plane',
    unit: 'mm',
    description: 'A vertical plane passing through the midline of the hips.',
  },
  [MetricKeyEnum.HipToEar]: {
    key: MetricKeyEnum.HipToEar,
    modelType: ModelTypeEnum.Standing,
    layerType: LayerTypeEnum.Sagittal,
    tableName: 'Hip to Ears',
    toggleName: 'Hip to Ears',
    layerKey: 'hip_to_ear',
    unit: '°',
    description:
      'The angle between the vertical axis and a line connecting the hips to the ears. This represents forward or backward leaning posture in the sagittal (side) view.',
  },
  [MetricKeyEnum.KneeBend]: {
    key: MetricKeyEnum.KneeBend,
    modelType: ModelTypeEnum.Standing,
    layerType: LayerTypeEnum.Sagittal,
    tableName: 'Knee Bend',
    toggleName: 'Knee Bend',
    layerKey: 'knee_bend',
    unit: 'mm',
    description:
      'The angle between the upper and lower leg segments. This shows the degree to which the knees are bent while standing.',
  },
  [MetricKeyEnum.AnkleFlexion]: {
    key: MetricKeyEnum.AnkleFlexion,
    modelType: ModelTypeEnum.Standing,
    layerType: LayerTypeEnum.Sagittal,
    tableName: 'Ankle Flexion',
    toggleName: 'Ankle Flexion',
    layerKey: 'ankle_flexion',
    unit: '°',
    description:
      'The angle at the ankle joint, representing dorsiflexion or plantarflexion. This shows how much the ankle is flexed while standing.',
  },
  [MetricKeyEnum.MidSagittalPlane]: {
    key: MetricKeyEnum.MidSagittalPlane,
    modelType: ModelTypeEnum.Standing,
    layerType: LayerTypeEnum.Sagittal,
    tableName: 'Mid Sagittal Plane',
    toggleName: 'Mid Sagittal Plane',
    layerKey: 'mid_sagittal_plane',
    unit: 'mm',
    description:
      'A vertical plane that divides the body into left and right halves in the sagittal view. Used as a reference for measuring sagittal alignment.',
  },
  [MetricKeyEnum.MaxUpperSagittal]: {
    key: MetricKeyEnum.MaxUpperSagittal,
    modelType: ModelTypeEnum.Standing,
    layerType: LayerTypeEnum.Sagittal,
    tableName: 'Max Upper Sagittal',
    toggleName: 'Max Upper Sagittal',
    layerKey: 'max_upper_sagittal',
    unit: '°',
    description: 'How much your upper back surface curves',
  },
  [MetricKeyEnum.MaxLowerSagittal]: {
    key: MetricKeyEnum.MaxLowerSagittal,
    modelType: ModelTypeEnum.Standing,
    layerType: LayerTypeEnum.Sagittal,
    tableName: 'Max Lower Sagittal',
    toggleName: 'Max Lower Sagittal',
    layerKey: 'max_lower_sagittal',
    unit: '°',
    description: 'How much your lower back surface curves',
  },
  [MetricKeyEnum.SagittalBalancePlane]: {
    key: MetricKeyEnum.SagittalBalancePlane,
    modelType: ModelTypeEnum.Standing,
    layerType: LayerTypeEnum.Sagittal,
    tableName: 'Sagittal Balance Plane',
    toggleName: 'Sagittal Balance Plane',
    layerKey: 'sagittal_balance_plane',
    unit: 'mm',
    description:
      'How far forward or backward your head is compared to your hips.',
  },
  [MetricKeyEnum.AiPredictedCobbAngle]: {
    key: MetricKeyEnum.AiPredictedCobbAngle,
    modelType: ModelTypeEnum.Bent,
    layerType: LayerTypeEnum.General,
    tableName: 'AI Predicted Cobb Angle',
    toggleName: 'AI Predicted Cobb Angle',
    layerKey: 'ai_predicted_cobb_angle',
    unit: '°',
    description:
      'An estimated measurement of spinal curvature based on AI analysis. The Cobb angle is used to assess the severity of scoliosis.',
  },
  [MetricKeyEnum.SurfaceSpine]: {
    key: MetricKeyEnum.SurfaceSpine,
    modelType: ModelTypeEnum.Standing,
    layerType: LayerTypeEnum.General,
    tableName: 'Surface Spine',
    toggleName: 'Surface Spine',
    layerKey: 'surface_spine',
    unit: '°',
    description:
      'The angle between the vertical axis and a line connecting the hips to the ears. This represents forward or backward leaning posture in the sagittal (side) view.',
  },
  [MetricKeyEnum.CenterOfMass]: {
    key: MetricKeyEnum.CenterOfMass,
    modelType: ModelTypeEnum.Standing,
    layerType: LayerTypeEnum.Sagittal,
    tableName: 'Center of Mass',
    toggleName: 'Center of Mass',
    layerKey: 'center_of_mass',
    unit: 'mm',
    description: "The average position of the body's mass.",
  },
  [MetricKeyEnum.CenterOfMassAboveHips]: {
    key: MetricKeyEnum.CenterOfMassAboveHips,
    modelType: ModelTypeEnum.Standing,
    layerType: LayerTypeEnum.Sagittal,
    tableName: 'Center of Mass Above Hips',
    toggleName: 'Center of Mass Above Hips',
    layerKey: 'center_of_mass_above_hips',
    unit: 'mm',
    description: "The average position of the body's mass above the hips.",
  },
} as const;

export interface Layer {
  key: MetricKeyEnum;
  visible: boolean;
  modelType: ModelTypeEnum;
  layerType: LayerTypeEnum;
}

export type LayerConfig = {
  key: MetricKeyEnum;
  visible: boolean;
};

interface LayerStoreProps {
  modelType: ModelTypeEnum;
  setModelType: (type: ModelTypeEnum) => void;
  layerState: { [key: string]: Layer };
  toggleLayer: (key: string) => void;
  addLayerState: (key: MetricKeyEnum, layer: LayerConfig) => void;
  removeLayerState: (key: string) => void;
  isLayerVisible: (key: string) => boolean;
  highlightLayer: (key: string | null) => void;
  highlightedLayer: string | null;
  setLayerToggleState: (key: string, visible: boolean) => void;
}

export const useLayerStore = create<LayerStoreProps>((set, get) => ({
  modelType: ModelTypeEnum.Standing,
  setModelType: (type: ModelTypeEnum) => set({ modelType: type }),
  layerState: {},
  toggleLayer: (key: string) =>
    set((state) => ({
      layerState: {
        ...state.layerState,
        [key]: {
          ...state.layerState[key],
          visible: !state.layerState[key].visible,
        },
      },
    })),
  setLayerToggleState: (key: string, visible: boolean) => {
    set((state) => ({
      layerState: {
        ...state.layerState,
        [key]: { ...state.layerState[key], visible },
      },
    }));
  },
  addLayerState: (key, layerOptions) => {
    const layers = get().layerState;
    if (layers[key]) {
      return;
    }
    const layerDefaults = metrics[key];
    if (!layerDefaults) {
      return;
    }
    const layer: Layer = {
      ...layerDefaults,
      ...layerOptions,
    };

    set((state) => ({
      layerState: {
        ...state.layerState,
        [key]: { ...layer },
      },
    }));
  },
  removeLayerState: (key: string) => {
    set((state) => {
      const newLayers = { ...state.layerState };
      delete newLayers[key];
      return { layerState: newLayers };
    });
  },
  isLayerVisible: (key: string) => {
    const state = get();
    const layer = state.layerState[key];
    return (
      Boolean(layer?.visible) && Boolean(layer?.modelType === state.modelType)
    );
  },
  highlightedLayer: null,
  highlightLayer: (key) => {
    set({ highlightedLayer: key });
  },
}));
